import { useState, useCallback, useEffect } from 'react'
import { eventBus, EVENTS } from '../../../../../utils/EventBus'
import { EMPTY_CONTEXT_MENU } from '../constants'
import { formatCode } from '../../../../../utils/formatCode'

export function useClipboard({
  distributionManager,
  columnManager,
  selectedItems,
  selectedRow,
  selectedColumn,
  setSelectedColumn,
  setNewColumnId,
  scrollToRight,
  contextMenu,
  closeContextMenu,
  onDeleteColumn,
  onHideRow,
  onHideHeaderField,
  onHideColumn
}) {
  const [clipboardInfo, setClipboardInfo] = useState(null)

  useEffect(() => {
    const updateClipboardInfo = () => {
      const info = distributionManager.clipboardManager?.getClipboardInfo()
      setClipboardInfo(info)
    }

    const unsubscribeCopy = eventBus.on(
      EVENTS.CLIPBOARD_COPY,
      updateClipboardInfo
    )
    const unsubscribeClear = eventBus.on(
      EVENTS.CLIPBOARD_CLEAR,
      updateClipboardInfo
    )

    return () => {
      unsubscribeCopy()
      unsubscribeClear()
    }
  }, [distributionManager])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'c' &&
        !e.target.matches('input, textarea')
      ) {
        e.preventDefault()

        if (selectedRow) {
          const customerCodes = columnManager.getCustomerCodes()
          distributionManager.clipboardManager.copyRow(
            selectedRow,
            distributionManager,
            customerCodes
          )
          setClipboardInfo(
            distributionManager.clipboardManager.getClipboardInfo()
          )
        } else if (selectedColumn) {
          distributionManager.clipboardManager.copyColumn(
            selectedColumn,
            selectedItems,
            distributionManager
          )
          setClipboardInfo(
            distributionManager.clipboardManager.getClipboardInfo()
          )
        }
      }

      if (
        (e.ctrlKey || e.metaKey) &&
        e.key === 'v' &&
        !e.target.matches('input, textarea')
      ) {
        e.preventDefault()

        const type = distributionManager.clipboardManager.getType()

        if (type === 'row' && selectedRow) {
          distributionManager.clipboardManager.pasteRow(
            selectedRow,
            distributionManager
          )
        } else if (type === 'column') {
          const newColumnCode =
            distributionManager.clipboardManager.pasteColumnAsNew(
              selectedItems,
              distributionManager,
              columnManager,
              selectedColumn
            )

          if (newColumnCode) {
            setNewColumnId(newColumnCode)
            setSelectedColumn(null)

            if (!selectedColumn) {
              scrollToRight()
            }
          }
        }
      }

      if (e.key === 'Escape') {
        closeContextMenu()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [
    selectedRow,
    selectedColumn,
    selectedItems,
    distributionManager,
    columnManager,
    setNewColumnId,
    setSelectedColumn,
    scrollToRight,
    closeContextMenu
  ])

  const handleCopyRow = useCallback(() => {
    if (contextMenu.data?.item) {
      const customerCodes = columnManager.getCustomerCodes()
      distributionManager.clipboardManager.copyRow(
        contextMenu.data.item,
        distributionManager,
        customerCodes
      )
      setClipboardInfo(distributionManager.clipboardManager.getClipboardInfo())
      closeContextMenu()
    }
  }, [contextMenu, distributionManager, columnManager, closeContextMenu])

  const handlePasteRow = useCallback(() => {
    if (contextMenu.data?.item) {
      distributionManager.clipboardManager.pasteRow(
        contextMenu.data.item,
        distributionManager
      )

      closeContextMenu()
    }
  }, [contextMenu, distributionManager, closeContextMenu])

  const handleCopyColumn = useCallback(() => {
    if (contextMenu.data?.customerCode) {
      distributionManager.clipboardManager.copyColumn(
        contextMenu.data.customerCode,
        selectedItems,
        distributionManager
      )
      setClipboardInfo(distributionManager.clipboardManager.getClipboardInfo())
      closeContextMenu()
    }
  }, [contextMenu, selectedItems, distributionManager, closeContextMenu])

  const handlePasteColumn = useCallback(() => {
    const afterCustomerCode = contextMenu.data?.customerCode

    const newColumnCode = distributionManager.clipboardManager.pasteColumnAsNew(
      selectedItems,
      distributionManager,
      columnManager,
      afterCustomerCode
    )

    if (newColumnCode) {
      setNewColumnId(newColumnCode)
      setSelectedColumn(null)

      closeContextMenu()
    }
  }, [
    contextMenu,
    selectedItems,
    distributionManager,
    columnManager,
    setNewColumnId,
    setSelectedColumn,
    closeContextMenu
  ])

  const handleInsertColumnLeft = useCallback(() => {
    if (!contextMenu.data?.customerCode) return

    const targetCustomerCode = contextMenu.data.customerCode
    const tempCode = `NEW_${Date.now()}`

    const success = columnManager.insertCustomerColumnBefore(
      targetCustomerCode,
      tempCode,
      ''
    )

    if (success) {
      distributionManager.addCustomerToItems(selectedItems, tempCode, '')
      setNewColumnId(tempCode)

      closeContextMenu()

      const col = columnManager.getColumnByCustomerCode(tempCode)
      const snapshot = {
        colId: col.id,
        col: { ...col },
        width: columnManager.getWidth(col.id),
        orderSnapshot: [...columnManager.order],
        headerFields: columnManager.getCustomerHeaderFields(tempCode)
      }

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${formatCode(targetCustomerCode)} の左に列を挿入しました`,
        up: () => {
          if (!columnManager.hasCustomerCode(tempCode)) {
            columnManager.restoreCustomerColumn(snapshot)
            distributionManager.addCustomerToItems(selectedItems, tempCode, '')
            eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
          }
        },
        down: () => {
          columnManager.removeCustomerColumn(tempCode)
          distributionManager.removeCustomerFromItems(selectedItems, tempCode)
        }
      })
    }
  }, [
    contextMenu,
    columnManager,
    distributionManager,
    selectedItems,
    setNewColumnId,
    closeContextMenu
  ])

  const handleInsertColumnRight = useCallback(() => {
    if (!contextMenu.data?.customerCode) return

    const targetCustomerCode = contextMenu.data.customerCode
    const tempCode = `NEW_${Date.now()}`

    const success = columnManager.insertCustomerColumnAfter(
      targetCustomerCode,
      tempCode,
      ''
    )

    if (success) {
      distributionManager.addCustomerToItems(selectedItems, tempCode, '')
      setNewColumnId(tempCode)

      closeContextMenu()

      const col = columnManager.getColumnByCustomerCode(tempCode)
      const snapshot = {
        colId: col.id,
        col: { ...col },
        width: columnManager.getWidth(col.id),
        orderSnapshot: [...columnManager.order],
        headerFields: columnManager.getCustomerHeaderFields(tempCode)
      }

      distributionManager.addLogEntry({
        action: `[${distributionManager.username}] ${formatCode(targetCustomerCode)} の右に列を挿入しました`,
        up: () => {
          if (!columnManager.hasCustomerCode(tempCode)) {
            columnManager.restoreCustomerColumn(snapshot)
            distributionManager.addCustomerToItems(selectedItems, tempCode, '')
            eventBus.emit(EVENTS.DISTRIBUTION_RESET, distributionManager.data)
          }
        },
        down: () => {
          columnManager.removeCustomerColumn(tempCode)
          distributionManager.removeCustomerFromItems(selectedItems, tempCode)
        }
      })
    }
  }, [
    contextMenu,
    columnManager,
    distributionManager,
    selectedItems,
    setNewColumnId,
    closeContextMenu
  ])

  const handleDeleteColumn = useCallback(() => {
    if (contextMenu.data?.customerCode) {
      if (onDeleteColumn) {
        onDeleteColumn(contextMenu.data.customerCode)
      }
      closeContextMenu()
    }
  }, [contextMenu, onDeleteColumn, closeContextMenu])

  const handleHideRow = useCallback(
    (keysOrEvent) => {
      const keys =
        Array.isArray(keysOrEvent) || typeof keysOrEvent === 'string'
          ? keysOrEvent
          : null
      if (keys) {
        if (onHideRow) onHideRow(keys, contextMenu.data?.item)
      } else if (contextMenu.data?.item) {
        const { hinmeiCode, niukabango } = contextMenu.data.item
        const rowKey = `${hinmeiCode}::${niukabango}`
        if (onHideRow) onHideRow(rowKey, contextMenu.data.item)
      }
      closeContextMenu()
    },
    [contextMenu, onHideRow, closeContextMenu]
  )

  const handleHideColumn = useCallback(
    (keysOrEvent) => {
      const keys =
        Array.isArray(keysOrEvent) || typeof keysOrEvent === 'string'
          ? keysOrEvent
          : null
      if (keys) {
        if (onHideColumn) onHideColumn(keys)
      } else if (contextMenu.data?.customerCode) {
        if (onHideColumn) onHideColumn(contextMenu.data.customerCode)
      }
      closeContextMenu()
    },
    [contextMenu, onHideColumn, closeContextMenu]
  )

  const handleHideHeaderField = useCallback(() => {
    const selectedKeys = contextMenu.data?.selectedKeys
    const fieldKey = contextMenu.data?.fieldKey
    const keysToHide = Array.isArray(selectedKeys) && selectedKeys.length > 0
      ? selectedKeys
      : fieldKey ? [fieldKey] : []

    if (keysToHide.length > 0) {
      if (onHideHeaderField) onHideHeaderField(keysToHide)
      closeContextMenu()
    }
  }, [contextMenu, onHideHeaderField, closeContextMenu])

  const clearClipboard = useCallback(() => {
    distributionManager.clipboardManager.clear()
    setClipboardInfo(null)
  }, [distributionManager])

  return {
    clipboardInfo,
    handleCopyRow,
    handlePasteRow,
    handleCopyColumn,
    handlePasteColumn,
    handleInsertColumnLeft,
    handleInsertColumnRight,
    handleDeleteColumn,
    handleHideRow,
    handleHideHeaderField,
    handleHideColumn,
    clearClipboard
  }
}
